// Temporary pre-launch homepage. The full marketplace homepage lives in
// `Home.js` and is preserved untouched — the route in App.js picks between
// the two via the admin-controlled `site_settings.homepage_mode` toggle
// (Admin dashboard → Settings → "Homepage mode"). No env var or redeploy
// is needed to switch modes.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, ArrowRight, Mail, Store } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import SEO, { organizationSchema, websiteSchema } from '../components/SEO';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Launch target is admin-controlled — no longer hardcoded. Fetched from
// `/api/settings/important-dates` on mount. Until the fetch resolves (or
// if it fails entirely) we fall back to `null` and hide the countdown so
// the marketing page never renders a wildly wrong time. Human-readable
// "9 August 2026, 12:00 UK time" line is rendered from the same fetch.
const DEFAULT_LAUNCH_UTC = null;

const pad = (n) => String(n).padStart(2, '0');

const useCountdown = (target) => {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, []);
    if (!target) return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: false, ready: false };
    const diff = Math.max(0, target.getTime() - now);
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((diff / (1000 * 60)) % 60);
    const seconds = Math.floor((diff / 1000) % 60);
    return { days, hours, minutes, seconds, expired: diff === 0, ready: true };
};

// Small helper: turn `{launch_date:"2026-08-09", launch_time:"12:00",
// launch_timezone:"Europe/London"}` into a human display string that
// matches the admin spec ("9 August 2026, 12:00 PM UK time"). Uses
// Intl on the timezone the admin chose so the label always reflects
// the source of truth, not the visitor's local clock.
const formatLaunchLabel = (dates) => {
    if (!dates || !dates.launch_utc) return null;
    try {
        const d = new Date(dates.launch_utc);
        const opts = {
            day: 'numeric', month: 'long', year: 'numeric',
            hour: 'numeric', minute: '2-digit', hour12: true,
            timeZone: dates.launch_timezone || 'Europe/London',
        };
        const zoneLabel = (dates.launch_timezone || 'Europe/London') === 'Europe/London' ? 'UK' : dates.launch_timezone;
        return `${d.toLocaleString('en-GB', opts)} ${zoneLabel} time`;
    } catch (_) {
        return null;
    }
};

const PreLaunchHome = () => {
    const [email, setEmail] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [subscribed, setSubscribed] = useState(false);
    // Read the last-known important-dates payload synchronously from
    // localStorage so returning visitors see the correct launch label
    // on the very first paint — no fetch-round-trip flicker.
    // Cache key is namespaced + tiny so it can't collide or bloat.
    const readCachedDates = () => {
        try {
            const raw = localStorage.getItem('bb_launch_dates_v1');
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            // Minimal shape sanity — must at least have launch_utc.
            if (parsed && typeof parsed === 'object' && parsed.launch_utc) return parsed;
        } catch (_) { /* corrupted / disallowed — treat as no cache */ }
        return null;
    };
    const cachedDates = readCachedDates();

    const [dates, setDates] = useState(cachedDates);
    // Loaded means "we have committed to a display state" — either from
    // cache (immediate) or from a completed fetch. Prevents the h1 from
    // rendering empty on returning visits.
    const [datesLoaded, setDatesLoaded] = useState(Boolean(cachedDates));
    const launchDate = dates?.launch_utc ? new Date(dates.launch_utc) : null;
    const { days, hours, minutes, seconds, ready } = useCountdown(launchDate);
    const launchLabel = formatLaunchLabel(dates);

    useEffect(() => {
        let cancelled = false;
        fetch(`${API_URL}/settings/important-dates`)
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => {
                if (cancelled || !d) return;
                setDates(d);
                try { localStorage.setItem('bb_launch_dates_v1', JSON.stringify(d)); } catch (_) { /* quota / disallowed */ }
            })
            .catch(() => { /* stay in loading state — never break the page */ })
            .finally(() => { if (!cancelled) setDatesLoaded(true); });
        return () => { cancelled = true; };
    }, []);

    const handleSubscribe = async (e) => {
        e.preventDefault();
        const v = email.trim();
        if (!v) {
            toast.error('Please enter your email');
            return;
        }
        setSubmitting(true);
        try {
            const res = await fetch(`${API_URL}/subscribe`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: v }),
            });
            if (res.ok) {
                setSubscribed(true);
                toast.success('You\'re on the list — we\'ll email you when Biscuit Brand launches.');
            } else {
                const d = await res.json().catch(() => ({}));
                toast.error(d.detail || 'Could not subscribe. Please try again.');
            }
        } catch (_) {
            toast.error('Network error. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <SEO
                title="Biscuit Brand | Global Biscuit Marketplace Opening 9 August 2026"
                description="Biscuit Brand is building the world's dedicated biscuit marketplace. Request biscuits, join the launch list, or register as a seller before opening on 9 August 2026."
                schema={[organizationSchema, websiteSchema]}
            />

            {/* Hero — warm biscuit/orange palette, mobile-first centred layout. */}
            <section
                className="relative overflow-hidden"
                style={{
                    background:
                        'radial-gradient(ellipse at top, rgba(255, 200, 140, 0.35), transparent 60%), linear-gradient(180deg, #fdf6ef 0%, #f8eadb 60%, #f1ddc4 100%)',
                }}
                data-testid="prelaunch-hero"
            >
                <div className="container-custom py-16 sm:py-24 lg:py-32">
                    <div className="max-w-3xl mx-auto text-center">
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/80 backdrop-blur-sm border border-amber-200 shadow-sm mb-6">
                            <Cookie className="w-4 h-4 text-amber-700" />
                            <span className="text-xs font-semibold tracking-wide uppercase text-amber-800">
                                Pre-launch
                            </span>
                        </div>

                        <h1
                            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight min-h-[3.5rem] sm:min-h-[4rem] lg:min-h-[4.5rem]"
                            style={{ color: '#3a2418', visibility: datesLoaded ? 'visible' : 'hidden' }}
                            data-testid="prelaunch-headline"
                        >
                            {/* Space is reserved via `min-h-*` above and the
                                headline itself is kept `visibility: hidden`
                                until the /important-dates fetch settles.
                                Nothing paints — no skeleton bar, no "Coming
                                Soon" flash, no layout jump — the hero simply
                                appears fully-formed once we know the answer. */}
                            {datesLoaded && (launchLabel ? `Biscuit Brand Opens ${launchLabel}` : 'Biscuit Brand — Coming Soon')}
                        </h1>

                        <p className="mt-5 text-lg sm:text-xl font-medium" style={{ color: '#6b4a32' }}>
                            We're building the world's dedicated biscuit marketplace.
                        </p>

                        <p className="mt-4 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed" style={{ color: '#7a6858' }}>
                            We're onboarding biscuit brands, adding shops, and preparing a better
                            way to discover biscuits from around the world.
                        </p>

                        {/* Countdown — kept dead simple to avoid hydration drift.
                            Hidden until admin dates fetch resolves so we never
                            flash a wrong number on first paint. */}
                        {ready && (
                        <div
                            className="mt-10 grid grid-cols-4 gap-3 sm:gap-4 max-w-md mx-auto"
                            data-testid="prelaunch-countdown"
                            aria-label="Time until launch"
                        >
                            {[
                                { label: 'Days', value: days },
                                { label: 'Hours', value: hours },
                                { label: 'Minutes', value: minutes },
                                { label: 'Seconds', value: seconds },
                            ].map((u) => (
                                <div
                                    key={u.label}
                                    className="rounded-xl bg-white/85 backdrop-blur-sm border border-amber-200 shadow-sm py-3"
                                >
                                    <div className="font-serif text-2xl sm:text-3xl font-bold" style={{ color: '#3a2418' }}>
                                        {pad(u.value)}
                                    </div>
                                    <div className="text-[10px] sm:text-xs uppercase tracking-wide font-medium" style={{ color: '#9a7a5a' }}>
                                        {u.label}
                                    </div>
                                </div>
                            ))}
                        </div>
                        )}

                        {/* Primary CTAs */}
                        <div className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
                            <Button
                                size="lg"
                                className="rounded-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-md hover:shadow-lg transition-shadow font-medium"
                                onClick={() => {
                                    // Reuse the existing global Request-a-Biscuit FAB so submissions
                                    // land in the same demand intelligence collection.
                                    const fab = document.querySelector('[data-testid="request-fab"]');
                                    if (fab) {
                                        fab.click();
                                    } else {
                                        toast.info('Request flow is loading — please try again in a moment.');
                                    }
                                }}
                                data-testid="prelaunch-request-cta"
                            >
                                <Cookie className="w-4 h-4 mr-2" />
                                Request a Biscuit
                            </Button>
                            <Link to="/become-a-seller" data-testid="prelaunch-seller-cta">
                                <Button
                                    size="lg"
                                    variant="outline"
                                    className="rounded-full border-amber-300 text-amber-900 hover:bg-amber-50 w-full sm:w-auto"
                                >
                                    <Store className="w-4 h-4 mr-2" />
                                    Become a Seller
                                    <ArrowRight className="w-4 h-4 ml-2" />
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Newsletter + supporting notes */}
            <section className="py-14 sm:py-20" style={{ background: '#fffaf3' }}>
                <div className="container-custom max-w-2xl">
                    <div className="text-center">
                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 mb-4">
                            <Mail className="w-5 h-5 text-amber-700" />
                        </div>
                        <h2 className="font-serif text-2xl sm:text-3xl font-semibold" style={{ color: '#3a2418' }}>
                            Get notified when Biscuit Brand launches.
                        </h2>
                        <p className="mt-2 text-sm sm:text-base" style={{ color: '#7a6858' }}>
                            One email on launch day. No spam, ever.
                        </p>
                    </div>

                    <form
                        onSubmit={handleSubscribe}
                        className="mt-6 flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
                        data-testid="prelaunch-newsletter-form"
                    >
                        <Input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@example.com"
                            required
                            disabled={submitting || subscribed}
                            className="flex-1 h-11 bg-white border-amber-200 focus-visible:ring-amber-400"
                            data-testid="prelaunch-newsletter-email"
                        />
                        <Button
                            type="submit"
                            size="lg"
                            disabled={submitting || subscribed}
                            className="rounded-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-md whitespace-nowrap"
                            data-testid="prelaunch-newsletter-submit"
                        >
                            {subscribed ? 'Subscribed' : submitting ? 'Joining…' : 'Notify me'}
                        </Button>
                    </form>

                    <div className="mt-12 grid sm:grid-cols-2 gap-4 sm:gap-6">
                        <div className="rounded-2xl bg-white border border-amber-100 shadow-sm p-5">
                            <div className="flex items-center gap-2 mb-2">
                                <Store className="w-4 h-4 text-amber-700" />
                                <span className="text-xs font-semibold uppercase tracking-wide text-amber-800">
                                    For sellers
                                </span>
                            </div>
                            <p className="text-sm leading-relaxed" style={{ color: '#5e4936' }}>
                                Biscuit brands, bakeries, and manufacturers can still register
                                before launch.
                            </p>
                            <Link
                                to="/become-a-seller"
                                className="inline-flex items-center mt-3 text-sm font-medium text-amber-800 hover:text-amber-900"
                                data-testid="prelaunch-seller-link"
                            >
                                Register your shop <ArrowRight className="w-4 h-4 ml-1" />
                            </Link>
                        </div>
                        <div className="rounded-2xl bg-white border border-amber-100 shadow-sm p-5">
                            <div className="flex items-center gap-2 mb-2">
                                <Cookie className="w-4 h-4 text-amber-700" />
                                <span className="text-xs font-semibold uppercase tracking-wide text-amber-800">
                                    For buyers
                                </span>
                            </div>
                            <p className="text-sm leading-relaxed" style={{ color: '#5e4936' }}>
                                Looking for a biscuit? Tell us what you want and we'll use your
                                request to help source it.
                            </p>
                            <button
                                type="button"
                                className="inline-flex items-center mt-3 text-sm font-medium text-amber-800 hover:text-amber-900"
                                onClick={() => {
                                    const fab = document.querySelector('[data-testid="request-fab"]');
                                    if (fab) fab.click();
                                }}
                                data-testid="prelaunch-request-link"
                            >
                                Submit a biscuit request <ArrowRight className="w-4 h-4 ml-1" />
                            </button>
                        </div>
                    </div>

                    <div className="mt-10 text-center">
                        <Link
                            to="/blog"
                            className="text-sm font-medium text-amber-800 hover:text-amber-900 underline-offset-4 hover:underline"
                            data-testid="prelaunch-blog-link"
                        >
                            Read the Biscuit Brand blog →
                        </Link>
                    </div>
                </div>
            </section>
        </>
    );
};

export default PreLaunchHome;
